import React, { useState } from "react";
import clsx from "clsx";

import Avatar from "components/common/Avatar";
import SpaceGuestBadge from "./SpaceGuestBadge";
import MenuItem from "components/common/MenuItem";
import Dropdown from "components/common/Dropdown";
import IconButton from "components/common/IconButton";
import Divider from "components/common/Divider";
import { Space, SpaceMember } from "redux/Ldb/types/spaceTypes";
import { defaultSynthMember } from "./constants/defaultSynthMember";
import { getFromStorage } from "helpers";
import { normalizeMembers } from "../../utils/spaceUtils";
import SpaceDetailsRemoveMembersModal from "./SpaceDetailsRemoveMembersModal";
import useToggle from "hooks/useToggle";
import SearchTextField from "components/common/InputField/SearchTextField";

type SpaceDetailsMembersSidebarProps = {
    spaceData: Space | null;
};

export default function SpaceDetailsMembersSidebar(props: SpaceDetailsMembersSidebarProps) {
    const { spaceData } = props;
    const userProfile = getFromStorage("ally-user");
    const [search, setSearch] = useState("");

    const [openRemoveMembersModal, setOpenRemoveMembersModal] = useToggle();
    const [member, setMember] = useState<{ fullName: string; id: string; isGuest: boolean } | null>(
        null
    );

    const members = normalizeMembers(spaceData);

    const spaceMembers = spaceData?.members as SpaceMember[];
    const owner = spaceMembers.find((member) => member?.isOwner) as SpaceMember;
    const isOwner = owner?.ldbId === userProfile?.user?._id;

    return (
        <>
            <div className="w-full h-full overflow-hidden z-100">
                <p className="text-sm font-rocGroteskMedium font-semibold px-[13px] py-[19px]">
                    People in this spaces
                </p>
                <SearchTextField
                    inputPlaceholder="Search Members"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    inputContainerClassname="border-b !border-l-0 !border-r-0 !border-slate-100 !rounded-none !z-0 !overflow-hidden"
                />

                <div className="h-full overflow-y-scroll overflow-x-hidden scrollbar-hidden">
                    {members
                        .filter(
                            (member) =>
                                member?.fullName?.toLowerCase().includes(search.toLowerCase()) ||
                                member?.email?.toLowerCase().includes(search.toLowerCase())
                        )
                        .map((member, index) => {
                            return (
                                <div
                                    key={index}
                                    className="flex  w-full items-center gap-2 p-[12px] py-[8px] bg-white hover:bg-slate-100 px-[16px] transition-colors"
                                >
                                    <Avatar
                                        src={member?.avatar}
                                        alt={member?.fullName}
                                        className="w-[24px] h-[24px]"
                                    />
                                    <div>
                                        <p
                                            className={clsx(
                                                "text-sm font-rocGroteskMedium font-semibold truncate whitespace-nowrap w-[180px]",
                                                {
                                                    "text-[#62CC00]":
                                                        member?.fullName ===
                                                        defaultSynthMember.name,
                                                }
                                            )}
                                        >
                                            {member?.fullName || member?.email}
                                        </p>
                                        <p className="text-sm font-rocGroteskMedium text-slate-500">
                                            {member?.role}
                                        </p>
                                    </div>

                                    <div>
                                        {!!member?.isGuest && (
                                            <SpaceGuestBadge className="!text-[#0AAD0A] !bg-[#DCFCE7]" />
                                        )}
                                    </div>

                                    <div className="flex-1" />

                                    {!member?.isOwner &&
                                        !member?.isSynth &&
                                        isOwner &&
                                        !spaceData?.isArchived && (
                                            <Dropdown
                                                dropdownOptions={[
                                                    {
                                                        label: <MenuItem>Remove Member</MenuItem>,
                                                        value: null,
                                                        action: () => {
                                                            setMember({
                                                                fullName:
                                                                    member?.fullName ||
                                                                    member?.email,
                                                                id: member?.isGuest
                                                                    ? member?.guestId
                                                                    : member?.ldbId,
                                                                isGuest: member?.isGuest,
                                                            });
                                                            setOpenRemoveMembersModal();
                                                        },
                                                    },
                                                ]}
                                                dropdown={
                                                    <IconButton
                                                        color="secondary"
                                                        disabled={openRemoveMembersModal}
                                                    >
                                                        <i className="ri-more-2-fill"></i>
                                                    </IconButton>
                                                }
                                                wholeContainerClass="!w-fit max-sm:!w-full rounded-lg !right-[0px]"
                                                dropdownContainerClasses="shadow-none border border-slate-200 rounded-lg !w-full !max-w-full min-w-full border border-slate-100 rounded !max-h-fit !pt-0 !right-[0px]"
                                            />
                                        )}
                                </div>
                            );
                        })}
                </div>
            </div>

            {openRemoveMembersModal && (
                <SpaceDetailsRemoveMembersModal
                    spaceData={spaceData}
                    member={member}
                    open={openRemoveMembersModal}
                    closeModal={() => {
                        setOpenRemoveMembersModal();
                        setMember(null);
                    }}
                />
            )}
        </>
    );
}
